import React from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SEO } from '../components/SEO';
import { breadcrumbSchema } from '../utils/structuredData';
export function PrivacyPolicyPage() {
  const structuredData = [breadcrumbSchema([{
    name: 'Home',
    url: 'https://close-crm-agentur.de'
  }, {
    name: 'Privacy Policy',
    url: 'https://close-crm-agentur.de/en/privacy-policy'
  }])];
  return <>
      <SEO title="Privacy Policy" description="Privacy policy for Close CRM Agentur and CRM Kraft LLC." canonical="https://close-crm-agentur.de/en/privacy-policy" noindex={true} structuredData={structuredData} data-id="element-615" />
      <div className="min-h-screen bg-[#0a0a0a]" data-id="element-616">
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-lg border-b border-white/5" data-id="element-617">
          <nav className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-618">
            <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20 gap-2 sm:gap-4" data-id="element-619">
              <a href="/" className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 sm:flex-1" aria-label="Close CRM Agentur homepage" data-id="element-620">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#0ea5e9] flex items-center justify-center flex-shrink-0" data-id="element-621">
                  <span className="text-white font-bold text-xs sm:text-sm" data-id="element-622">
                    C
                  </span>
                </div>
                <span className="text-white font-bold text-sm sm:text-base lg:text-lg whitespace-nowrap" data-id="element-623">
                  Close CRM Agentur
                </span>
              </a>
              <LanguageSwitcher className="absolute right-3 sm:relative sm:right-0" data-id="element-624" />
              <a href="/" className="hidden sm:flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors text-sm flex-shrink-0" data-id="element-625">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-626" />
                Back to homepage
              </a>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-627">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-628">
            <div className="prose prose-invert prose-lg max-w-none" data-id="element-629">
              <h1 className="text-4xl font-bold text-white mb-8" data-id="element-630">
                Privacy Policy
              </h1>
              <div className="space-y-8 text-gray-300" data-id="element-631">
                <section data-id="element-632">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-633">
                    1. Privacy at a glance
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-634">
                    General information
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-635">
                    The following information provides a simple overview of what
                    happens to your personal data when you visit this website.
                    Personal data is any data that can be used to identify you
                    personally. Detailed information on data protection can be
                    found in this privacy policy.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-636">
                    Data collection on this website
                  </h3>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-637">
                    Who is responsible for data collection?
                  </h4>
                  <p className="leading-relaxed mb-4" data-id="element-638">
                    Data processing on this website is carried out by the
                    website operator. You can find the operator&apos;s contact
                    details in the “Data controller” section of this privacy
                    policy.
                  </p>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-639">
                    How do we collect your data?
                  </h4>
                  <p className="leading-relaxed mb-4" data-id="element-640">
                    Some data is collected when you provide it to us, for
                    example when you contact us. Other data is collected
                    automatically or with your consent when you visit the
                    website. This is primarily technical data such as your
                    browser, operating system, or the time you accessed the
                    page.
                  </p>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-641">
                    What do we use your data for?
                  </h4>
                  <p className="leading-relaxed mb-4" data-id="element-642">
                    Some data is collected to ensure the website is provided
                    without errors. Other data may be used to analyse how you
                    use the website.
                  </p>
                  <h4 className="text-lg font-medium text-white mb-2" data-id="element-643">
                    What rights do you have?
                  </h4>
                  <p className="leading-relaxed" data-id="element-644">
                    You have the right to receive information about the origin,
                    recipient, and purpose of your stored personal data free of
                    charge at any time. You also have the right to request
                    correction or deletion of this data, withdraw consent for
                    future processing, request restriction of processing, and
                    lodge a complaint with the competent supervisory authority.
                  </p>
                </section>

                <section data-id="element-645">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-646">
                    2. Hosting
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-647">
                    Webflow
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-648">
                    We host our website with Webflow, Inc., 398 11th Street, 2nd
                    Floor, San Francisco, CA 94103, USA. When you visit our
                    website, Webflow may collect log files including your IP
                    address.
                  </p>
                  <p className="leading-relaxed mb-4" data-id="element-649">
                    Webflow is used to create and host websites. It may store
                    cookies or similar technologies necessary for displaying the
                    website, providing specific functions, and ensuring
                    security.
                  </p>
                  <p className="leading-relaxed" data-id="element-650">
                    For more information, please see Webflow&apos;s privacy
                    policy:{' '}
                    <a href="https://webflow.com/legal/eu-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] hover:underline" data-id="element-651">
                      https://webflow.com/legal/eu-privacy-policy
                    </a>
                    .
                  </p>
                </section>

                <section data-id="element-652">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-653">
                    3. General information and mandatory disclosures
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-654">
                    Data protection
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-655">
                    We treat your personal data confidentially and in accordance
                    with applicable data protection laws and this privacy
                    policy. Please note that data transmission on the internet,
                    for example through email communications, may have security
                    gaps.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-656">
                    Data controller
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-657">
                    The data controller responsible for processing on this
                    website is:
                  </p>
                  <p className="leading-relaxed mb-4" data-id="element-658">
                    CRM Kraft LLC
                    <br data-id="element-659" />
                    Represented by: Justus Kraft
                    <br data-id="element-660" />
                    7901 4th St. N, Ste. 300
                    <br data-id="element-661" />
                    St. Petersburg, FL 33702
                    <br data-id="element-662" />
                    United States of America
                    <br data-id="element-663" />
                    Phone:{' '}
                    <a href="tel:+4915735999071" className="text-[#0ea5e9] hover:underline" data-id="element-664">
                      +49 157 35999071
                    </a>
                    <br data-id="element-665" />
                    Email:{' '}
                    <a href="mailto:justus@crm-kraft.de" className="text-[#0ea5e9] hover:underline" data-id="element-666">
                      justus@crm-kraft.de
                    </a>
                  </p>
                  <p className="leading-relaxed mb-4" data-id="element-667">
                    The data controller is the natural or legal person who alone
                    or jointly with others determines the purposes and means of
                    processing personal data.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-668">
                    Storage period
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-669">
                    Unless a more specific storage period is stated in this
                    privacy policy, your personal data will remain with us until
                    the purpose for processing no longer applies. If you request
                    deletion or withdraw consent, we will delete your data
                    unless there are legally permitted reasons to retain it.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-670">
                    SSL/TLS encryption
                  </h3>
                  <p className="leading-relaxed" data-id="element-671">
                    This website uses SSL/TLS encryption to protect the
                    transmission of confidential content. You can recognise an
                    encrypted connection when your browser&apos;s address bar
                    changes from “http://” to “https://” and displays a lock
                    symbol.
                  </p>
                </section>

                <section data-id="element-672">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-673">
                    4. Data collection on this website
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-674">
                    Cookies
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-675">
                    Our website uses cookies. Cookies are small text files that
                    do not harm your device. They may be stored temporarily for
                    a session or permanently until you delete them or your
                    browser deletes them automatically.
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-676">
                    Contact requests
                  </h3>
                  <p className="leading-relaxed" data-id="element-677">
                    If you contact us by email or phone, we store and process
                    the information you provide, including your contact details,
                    to handle your request and any follow-up questions. We do
                    not share this information without your consent.
                  </p>
                </section>

                <section data-id="element-678">
                  <h2 className="text-2xl font-bold text-white mb-4" data-id="element-679">
                    5. Plugins and tools
                  </h2>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-680">
                    YouTube
                  </h3>
                  <p className="leading-relaxed mb-4" data-id="element-681">
                    This website may embed videos from YouTube. The provider is
                    Google Ireland Limited, Gordon House, Barrow Street, Dublin
                    4, Ireland. Further information is available in
                    Google&apos;s privacy policy:{' '}
                    <a href="https://policies.google.com/privacy?hl=en" target="_blank" rel="noopener noreferrer" className="text-[#0ea5e9] hover:underline" data-id="element-682">
                      https://policies.google.com/privacy?hl=en
                    </a>
                    .
                  </p>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-683">
                    Google Web Fonts
                  </h3>
                  <p className="leading-relaxed" data-id="element-684">
                    This website uses web fonts provided by Google to display
                    fonts consistently. When you open a page, your browser loads
                    the required web fonts into its cache so that text and fonts
                    display correctly.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-[#0a0a0a] border-t border-white/5 py-8" data-id="element-685">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-686">
            <p className="text-gray-600 text-sm" data-id="element-687">
              © {new Date().getFullYear()} Close CRM Agentur. All rights
              reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}